<!--
  Approach Three Images Component

  A features section component that displays a heading, description text, and three feature cards
  with optional background images. Each card includes a title and description text.

  This component adapts its colors based on the site_context prop to match different clinic themes.
-->
<template>
  <div class="flex flex-col gap-8 items-center justify-center overflow-hidden px-4 py-12 lg:px-16 lg:py-16 relative w-full bg-white">
    <div class="flex flex-col gap-12 items-start justify-center relative shrink-0 w-full max-w-[1200px]">
      <!-- Heading Section -->
      <div class="flex flex-col gap-5 items-start justify-center relative shrink-0 w-full">
        <div class="flex items-start relative shrink-0 w-full">
          <div class="flex-1 flex flex-col items-start">
            <div class="flex flex-col gap-1 items-start leading-none not-italic relative shrink-0 w-full">
              <!-- Overline -->
              <div class="flex flex-col justify-center relative shrink-0 text-sm tracking-wide uppercase w-full font-sans font-semibold">
                <p class="leading-5">{{ overlineText }}</p>
              </div>
              <!-- Main Heading -->
              <div class="flex flex-col justify-center relative shrink-0 text-4xl tracking-tight w-full font-sans font-medium">
                <p class="leading-11">{{ headingText }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Description Paragraphs -->
        <div class="flex flex-col justify-center leading-none not-italic relative shrink-0 text-gray-900 text-lg w-full font-sans font-normal">
          <p class="leading-7">{{ descriptionParagraph1 }}</p>
        </div>
        <div v-if="descriptionParagraph2" class="flex flex-col justify-center leading-none not-italic relative shrink-0 text-gray-900 text-lg w-full font-sans font-normal">
          <p class="leading-7" v-html="descriptionParagraph2" />
        </div>
      </div>

      <!-- Feature Cards -->
      <div class="flex flex-col lg:flex-row gap-6 lg:gap-8 h-auto lg:h-[300px] items-start justify-center relative shrink-0 w-full">
        <!-- Left Card -->
        <div class="w-full lg:flex-1 flex gap-6 h-[300px] items-end justify-center p-4 relative rounded-xl shrink-0">
          <div class="absolute inset-0 pointer-events-none rounded-xl">
            <div :class="`absolute inset-0 rounded-xl ${backgroundColorClass}`" />
            <div v-if="leftCardBackgroundImage" class="absolute inset-0 overflow-hidden rounded-xl">
              <img :src="leftCardBackgroundImage" alt="" class="absolute h-full w-auto max-w-none object-cover" style="left: -9.42%; width: 118.84%;">
            </div>
          </div>
          <div :class="`flex flex-col gap-1 items-start not-italic p-3 relative rounded-xl shrink-0 w-full bg-white border ${cardBorderClass} text-neutral-900`">
            <p class="leading-7 relative shrink-0 text-lg w-full font-sans font-semibold">
              {{ leftCardTitle }}
            </p>
            <p class="leading-6 relative shrink-0 text-base w-full font-sans font-normal">
              {{ leftCardDescription }}
            </p>
          </div>
        </div>

        <!-- Center Card -->
        <div class="w-full lg:flex-1 flex gap-6 h-[300px] items-end justify-center p-4 relative rounded-xl shrink-0">
          <div class="absolute inset-0 pointer-events-none rounded-xl">
            <div :class="`absolute inset-0 rounded-xl ${backgroundColorClass}`" />
            <div v-if="centerCardBackgroundImage" class="absolute inset-0 overflow-hidden rounded-xl">
              <img :src="centerCardBackgroundImage" alt="" class="absolute h-full w-auto max-w-none object-cover" style="left: -18.93%; top: -27%; width: 137.85%; height: 116%;">
            </div>
          </div>
          <div :class="`flex flex-col gap-1 items-start not-italic p-3 relative rounded-xl shrink-0 w-full bg-white border ${cardBorderClass} text-neutral-900`">
            <p class="leading-7 relative shrink-0 text-lg w-full font-sans font-semibold">
              {{ centerCardTitle }}
            </p>
            <p class="leading-6 relative shrink-0 text-base w-full font-sans font-normal">
              {{ centerCardDescription }}
            </p>
          </div>
        </div>

        <!-- Right Card -->
        <div class="w-full lg:flex-1 flex gap-6 h-[300px] items-end justify-center p-4 relative rounded-xl shrink-0">
          <div class="absolute inset-0 pointer-events-none rounded-xl">
            <div :class="`absolute inset-0 rounded-xl ${backgroundColorClass}`" />
            <div v-if="rightCardBackgroundImage" class="absolute inset-0 overflow-hidden rounded-xl">
              <img :src="rightCardBackgroundImage" alt="" class="absolute max-w-none object-cover rounded-xl size-full" style="object-position: 50% 50%;">
            </div>
          </div>
          <div :class="`flex flex-col gap-1 items-start not-italic p-3 relative rounded-xl shrink-0 w-full bg-white border ${cardBorderClass} text-neutral-900`">
            <p class="leading-7 relative shrink-0 text-lg w-full font-sans font-semibold">
              {{ rightCardTitle }}
            </p>
            <p class="leading-6 relative shrink-0 text-base w-full font-sans font-normal">
              {{ rightCardDescription }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // eslint-disable-next-line vue/prop-name-casing
  site_context: {
    type: String,
    default: null,
  },
  overlineText: {
    type: String,
    default: 'KLINIKA PRZYCZYNOWEGO LECZENIA NIEPŁODNOŚCI'
  },
  headingText: {
    type: String,
    default: 'Nasze podejście'
  },
  descriptionParagraph1: {
    type: String,
    default: 'Niepowodzenie w prokreacji może wynikać z różnorodnych przyczyn. Mają na nie wpływ nie tylko klasyczne zaburzenia układu rozrodczego czy endokrynnego (hormonalnego), ale współcześnie coraz większe znaczenie w zaburzeniach płodności odgrywają czynniki środowiskowe, cywilizacyjne, czy nawet styl życia. Wymaga to interdyscyplinarnego, wieloaspektowego spojrzenia na zdrowie prokreacyjne pacjenta.'
  },
  descriptionParagraph2: {
    type: String,
    default: ''
  },
  leftCardTitle: {
    type: String,
    default: 'Personalizacja'
  },
  leftCardDescription: {
    type: String,
    default: 'W stosunku do każdej pary podchodzimy w sposób spersonalizowany.'
  },
  leftCardBackgroundImage: {
    type: String,
    default: null
  },
  centerCardTitle: {
    type: String,
    default: 'Leczenie'
  },
  centerCardDescription: {
    type: String,
    default: 'Koncentrujemy się na leczeniu przyczyny, a nie tylko jej objawów.'
  },
  centerCardBackgroundImage: {
    type: String,
    default: null
  },
  rightCardTitle: {
    type: String,
    default: 'Zdrowie'
  },
  rightCardDescription: {
    type: String,
    default: 'Skupiamy się nie tylko na uzyskaniu efektu w postaci ciąży, ale także mamy na względzie zdrowie przyszłego dziecka i rodziny.'
  },
  rightCardBackgroundImage: {
    type: String,
    default: null
  }
})

// Color mapping based on site_context.
// Figma: the image sits on the clinic's primary tint (#81B3CD for KPLN),
// while the floating text box is white with a thin primary-colored border.
const backgroundColorClass = computed(() => {
  const colorMap = {
    'infertility_treatment_clinic': 'bg-infertility-clinic',
    'family_clinic': 'bg-family-clinic',
    'institute': 'bg-institute',
  }
  return colorMap[props.site_context] || 'bg-infertility-clinic'
})

const cardBorderClass = computed(() => {
  const colorMap = {
    'infertility_treatment_clinic': 'border-infertility-clinic',
    'family_clinic': 'border-family-clinic',
    'institute': 'border-institute',
  }
  return colorMap[props.site_context] || 'border-infertility-clinic'
})
</script>

<style scoped>
</style>
